import React, { useState, useRef, useEffect, useMemo } from "react";
import type {
  Task,
  User,
  Tag,
  TaskUpdateDto,
  RoomStatus,
  Reminder,
} from "../types/types.ts";
import AddIcon from "@mui/icons-material/Add";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { isoToYmd } from "../utils/date.ts";
import { TaskDetailPanel } from "./TaskDetailPanel.tsx";
import {
  PaletteColor,
  PaletteColorHex,
  PaletteColorDarkHex,
  PaletteColorDarkBg,
} from "../types/PaletteColor.ts";
import { useDarkMode } from "../hooks/useDarkMode.ts";
import { triggerEffect } from "./effects.ts";

interface FlatTaskItem {
  task: Task;
  parentTask?: Task;
}

interface TaskCardProps {
  task: Task;
  parentTask?: Task;
  onDragStart: (e: React.DragEvent<HTMLDivElement>, taskId: string) => void;
  onShowDetail: (task: Task, parentTask?: Task) => void;
  isDarkMode: boolean;
  hideSubtasks?: boolean;
}

const TaskCard: React.FC<TaskCardProps> = ({
  task,
  parentTask,
  onDragStart,
  onShowDetail,
  isDarkMode,
  hideSubtasks,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const formatDateRange = (s?: string | null, e?: string | null) => {
    const start = isoToYmd(s || "");
    if (!start) return "未設定";
    const end = isoToYmd(e || "");
    return end && end !== start ? `${start} ~ ${end}` : start;
  };

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, task.id)}
      className={`p-3 rounded-md shadow-sm border cursor-grab active:cursor-grabbing ${
        isDarkMode ? "bg-gray-800 border-gray-600" : "bg-white border-gray-200"
      }`}
    >
      {parentTask && (
        <div
          className={`text-xs mb-1 px-1.5 py-0.5 rounded inline-block ${
            isDarkMode
              ? "bg-gray-700 text-gray-400"
              : "bg-gray-100 text-gray-500"
          }`}
        >
          {parentTask.title}
        </div>
      )}
      <div className="flex justify-between items-start">
        <span
          className={`text-sm font-semibold break-words ${
            isDarkMode ? "text-gray-100" : "text-gray-800"
          }`}
        >
          {task.title}
        </span>
        <button
          onClick={() => onShowDetail(task, parentTask)}
          className={`flex-shrink-0 ml-2 ${
            isDarkMode
              ? "text-gray-400 hover:text-blue-400"
              : "text-gray-400 hover:text-blue-500"
          }`}
        >
          <MoreHorizIcon fontSize="small" />
        </button>
      </div>
      <div
        className={`text-xs mt-2 space-y-1 ${
          isDarkMode ? "text-gray-400" : "text-gray-500"
        }`}
      >
        <div className="flex flex-wrap items-center gap-x-2">
          {(task.tags || []).map((tag) => (
            <span
              key={tag.id}
              style={{ color: tag.color }}
              className="font-semibold"
            >
              #{tag.name}
            </span>
          ))}
        </div>
        {task.completed && task.completedAt ? (
          <>
            <p>完了者: {task.completedBy?.name || "情報なし"}</p>
            <p>完了日: {isoToYmd(task.completedAt)}</p>
          </>
        ) : (
          <>
            <p>
              担当:
              {(task.assignees || []).map((a) => a.name).join(", ") || "未設定"}
            </p>
            <p>期限: {formatDateRange(task.startDate, task.endDate)}</p>
          </>
        )}
      </div>
      {!hideSubtasks && task.type === "multi" && (task.childTasks || []).length > 0 && (
        <div className="mt-2">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={`text-xs flex items-center space-x-1 ${
              isDarkMode ? "text-blue-400" : "text-blue-600"
            }`}
          >
            <span>サブタスク</span>
            <ExpandMoreIcon
              fontSize="small"
              className={`transition-transform ${
                isExpanded ? "rotate-180" : ""
              }`}
            />
          </button>
          {isExpanded && (
            <ul
              className={`list-disc list-inside text-xs mt-1 pl-2 space-y-1 ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              {(task.childTasks || []).map((child) => (
                <li key={child.id}>{child.title}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

interface ConfettiParticleProps {
  id: number;
  style: React.CSSProperties;
}
const ConfettiParticle: React.FC<ConfettiParticleProps> = ({ id, style }) => (
  <div key={id} style={style}></div>
);

interface TaskBoardViewProps {
  tasks: Task[];
  onUpdateStatus: (
    taskId: string,
    status: Task["status"],
    options?: { keepVisible?: boolean }
  ) => void;
  fetchAndMergeTasks: (status: "完了" | "非表示") => void;
  onAddTask: (status: Task["status"]) => void;
  onUpdateTask: (taskId: string, updates: TaskUpdateDto) => Promise<void>;
  onDeleteTask: (taskId: string) => Promise<void>;
  roomMembers: User[];
  availableTags: Tag[];
  onManageTags: () => void;
  color: PaletteColor;
  addReminder: (
    taskId: string,
    reminderDateTime: Date
  ) => Promise<Reminder | null>;
  deleteReminder: (reminderId: string | number) => Promise<boolean>;
  roomStatuses: RoomStatus[];
  onShowHiddenColumn: (status: "完了" | "非表示") => void;
  forceReload: () => void;
  honbuId: number;
  roomId: number;
  onOpenRecurrenceManagement?: (
    selectedTaskId?: string,
    selectedRuleId?: number,
  ) => void;
}

export const TaskBoardView: React.FC<TaskBoardViewProps> = ({
  tasks,
  onUpdateStatus,
  fetchAndMergeTasks,
  onAddTask,
  onUpdateTask,
  onDeleteTask,
  roomMembers,
  availableTags,
  onManageTags,
  color,
  addReminder,
  deleteReminder,
  onShowHiddenColumn,
  roomStatuses,
  forceReload,
  honbuId,
  roomId,
  onOpenRecurrenceManagement,
}) => {
  const isDarkMode = useDarkMode();
  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null);
  const [confetti, setConfetti] = useState<ConfettiParticleProps[]>([]);
  const rootRef = useRef<HTMLDivElement>(null);
  const boardRef = useRef<HTMLDivElement>(null);
  const [availableHeight, setAvailableHeight] = useState<number | null>(null);
  const [showMainTasks, setShowMainTasksRaw] = useState(
    () => localStorage.getItem("board_showMainTasks") !== "false"
  );
  const [showSubtasks, setShowSubtasksRaw] = useState(
    () => localStorage.getItem("board_showSubtasks") === "true"
  );

  const setShowMainTasks = (next: boolean) => {
    localStorage.setItem("board_showMainTasks", String(next));
    setShowMainTasksRaw(next);
  };

  const setShowSubtasks = (next: boolean) => {
    localStorage.setItem("board_showSubtasks", String(next));
    setShowSubtasksRaw(next);
  };

  const [detailTask, setDetailTask] = useState<{
    task: Task;
    parentTask?: Task;
  } | null>(null);

  // ダークモード対応のカラー取得関数
  const getStatusColor = (statusColor: PaletteColor) => {
    return isDarkMode
      ? PaletteColorDarkHex[statusColor]
      : PaletteColorHex[statusColor];
  };

  const getStatusBgColor = (statusColor: PaletteColor) => {
    return isDarkMode ? PaletteColorDarkBg[statusColor] : undefined;
  };

  useEffect(() => {
    const calc = () => {
      if (!rootRef.current) return;
      const rect = rootRef.current.getBoundingClientRect();
      const bottomGap = 8;
      const h = window.innerHeight - rect.top - bottomGap;
      setAvailableHeight(h > 240 ? h : 240);
    };
    calc();
    window.addEventListener("resize", calc);
    window.addEventListener("orientationchange", calc);
    return () => {
      window.removeEventListener("resize", calc);
      window.removeEventListener("orientationchange", calc);
    };
  }, []);

  const onDragStart = (e: React.DragEvent<HTMLDivElement>, taskId: string) => {
    setDraggedTaskId(taskId);
    e.dataTransfer.effectAllowed = "move";
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const statuses = useMemo(
    () => (roomStatuses || []).map((s) => s.statusName as Task["status"]),
    [roomStatuses]
  );

  const tasksByStatus = useMemo(
    () =>
      statuses.reduce((acc, status) => {
        const items: FlatTaskItem[] = [];

        if (showMainTasks) {
          tasks
            .filter((t) => t.status === status)
            .forEach((t) => items.push({ task: t }));
        }

        if (showSubtasks) {
          tasks.forEach((parent) => {
            (parent.childTasks || []).forEach((child) => {
              if (child.status === status) {
                items.push({ task: child, parentTask: parent });
              }
            });
          });
        }

        acc[status] = items;
        return acc;
      }, {} as Record<string, FlatTaskItem[]>),
    [tasks, statuses, showMainTasks, showSubtasks]
  );

  const onDrop = (
    e: React.DragEvent<HTMLDivElement>,
    newStatus: Task["status"]
  ) => {
    e.preventDefault();
    if (draggedTaskId) {
      const keepVisible = newStatus === "完了" || newStatus === "非表示";
      onUpdateStatus(draggedTaskId, newStatus, { keepVisible });

      const statusInfo = roomStatuses.find((s) => s.statusName === newStatus);
      if (statusInfo?.action && statusInfo.action !== "None") {
        triggerEffect(statusInfo.action, { x: e.clientX, y: e.clientY });
      }
    }
    setDraggedTaskId(null);
  };

  return (
    <div
      ref={rootRef}
      className="relative flex flex-col w-full overflow-hidden pt-12"
      style={availableHeight ? { height: `${availableHeight}px` } : {}}
    >
      {/* メインタスク・サブタスク表示切替ボタン */}
      <div className="absolute top-1.5 right-2 z-20 flex items-center gap-1.5">
        <button
          onClick={() => setShowMainTasks(!showMainTasks)}
          className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors border ${
            showMainTasks
              ? isDarkMode
                ? "bg-blue-600 border-blue-600 text-white"
                : "bg-blue-600 border-blue-600 text-white"
              : isDarkMode
              ? "bg-transparent border-gray-600 text-gray-400"
              : "bg-transparent border-gray-300 text-gray-400"
          }`}
        >
          メインタスク
        </button>
        <button
          onClick={() => setShowSubtasks(!showSubtasks)}
          className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors border ${
            showSubtasks
              ? isDarkMode
                ? "bg-orange-500 border-orange-500 text-white"
                : "bg-orange-500 border-orange-500 text-white"
              : isDarkMode
              ? "bg-transparent border-gray-600 text-gray-400"
              : "bg-transparent border-gray-300 text-gray-400"
          }`}
        >
          サブタスク
        </button>
      </div>

      <div
        ref={boardRef}
        className="relative flex flex-1 w-full space-x-4 overflow-x-auto overflow-y-hidden p-2"
      >
        {statuses.map((status) => {
          const statusInfo = roomStatuses.find((s) => s.statusName === status);
          if (!statusInfo) return null;
          const statusColorHex = getStatusColor(statusInfo.color);
          return (
            <div
              key={status}
              className="flex-shrink-0 w-72 rounded-lg flex flex-col max-h-full"
              style={{
                backgroundColor: isDarkMode
                  ? PaletteColorDarkBg[statusInfo.color]
                  : `rgba(${parseInt(
                      PaletteColorHex[statusInfo.color].slice(1, 3),
                      16
                    )}, ${parseInt(
                      PaletteColorHex[statusInfo.color].slice(3, 5),
                      16
                    )}, ${parseInt(
                      PaletteColorHex[statusInfo.color].slice(5, 7),
                      16
                    )}, 0.1)`,
              }}
            >
              <h3
                className="text-md font-bold p-3 text-white rounded-t-lg"
                style={{ backgroundColor: statusColorHex }}
              >
                {status} ({tasksByStatus[status]?.length || 0})
              </h3>
              <div
                onDragOver={onDragOver}
                onDrop={(e) => onDrop(e, status)}
                className="flex-1 min-h-0 p-2 space-y-3 overflow-y-auto"
              >
                {(tasksByStatus[status] || []).map((item) => (
                  <TaskCard
                    key={item.parentTask ? `sub-${item.task.id}` : item.task.id}
                    task={item.task}
                    parentTask={item.parentTask}
                    onDragStart={onDragStart}
                    isDarkMode={isDarkMode}
                    hideSubtasks={showSubtasks}
                    onShowDetail={(task, parentTask) =>
                      setDetailTask({ task, parentTask })
                    }
                  />
                ))}

                {(status === "完了" || status === "非表示") &&
                  (tasksByStatus[status] || []).length === 0 && (
                    <div className="text-center p-4">
                      <button
                        onClick={() => onShowHiddenColumn(status)}
                        className={`text-sm hover:underline ${
                          isDarkMode ? "text-blue-400" : "text-blue-600"
                        }`}
                      >
                        {status}タスクを表示する
                      </button>
                    </div>
                  )}

                {status !== "完了" && status !== "非表示" && (
                  <button
                    onClick={() => onAddTask(status)}
                    className={`w-full flex items-center justify-center p-2 text-sm rounded-md ${
                      isDarkMode
                        ? "text-gray-300 hover:bg-gray-600"
                        : "text-gray-600 hover:bg-gray-300"
                    }`}
                  >
                    <AddIcon fontSize="small" className="mr-1" />
                    タスクを追加
                  </button>
                )}
              </div>
            </div>
          );
        })}

        <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
          {confetti.map((c) => (
            <ConfettiParticle key={c.id} {...c} />
          ))}
        </div>
      </div>

      {detailTask && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex justify-end"
          onClick={() => setDetailTask(null)}
        >
          <div
            className="w-96 h-full bg-white shadow-lg animate-slide-in-right"
            onClick={(e) => e.stopPropagation()}
          >
            <TaskDetailPanel
              task={detailTask.task}
              isSubtask={!!detailTask.parentTask}
              parentForContext={detailTask.parentTask ?? detailTask.task}
              onUpdateTask={onUpdateTask}
              onUpdateStatus={onUpdateStatus}
              onDeleteTask={onDeleteTask}
              roomMembers={roomMembers}
              availableTags={availableTags}
              onManageTags={onManageTags}
              color={color}
              addReminder={addReminder}
              deleteReminder={deleteReminder}
              onClose={() => setDetailTask(null)}
              displayMode="overlay"
              roomStatuses={roomStatuses}
              forceReload={forceReload}
              honbuId={honbuId}
              roomId={roomId}
              onOpenRecurrenceManagement={onOpenRecurrenceManagement}
            />
          </div>
        </div>
      )}
    </div>
  );
};
